<template>
  <div class="!mb-6">
    <div class="flex justify-between items-center">
      <div class="flex items-center whitespace-nowrap">
        <Icon
          v-if="agreementData.markers.archived"
          v-tippy="{ content: 'Archived' }"
          type="archive"
          class="mr-3"
        />
        <Dropdown
          position="bottom-left"
          :is-disabled="
            !$can('manage_agreement') || agreementData.status == 'Ended' || agreementData.markers.archived
              ? true
              : false
          "
        >
          <template #triggerContent>
            <div class="flex items-center cursor-pointer mr-4">
              <h3
                v-tippy="{ content: 'Status' }"
                class="font-bold flex items-center gap-2"
                :class="`text-${getDictionaryItem('agreementStatus', agreementData.status)?.color}`"
              >
                <Icon :type="getDictionaryItem('agreementStatus', agreementData.status)?.icon" />
                {{ $t('ui.entities.agreement.status.' + agreementData.status.toLowerCase()) }}
              </h3>
              <Icon
                v-if="$can('manage_agreement') && !agreementData.markers.archived"
                type="chevron-down"
                class="ml-2 text-md text-secondary"
                :class="agreementData.status == 'Ended' || agreementData.status == 'Archived' ? '!hidden' : ''"
              />
            </div>
          </template>
          <DropdownItem @item-clicked="$emit('status-changed', 'Unresponsive')">
            {{ $t('ui.entities.agreement.status.unresponsive') }}
          </DropdownItem>
          <DropdownItem @item-clicked="$emit('status-changed', 'Uncontactable')">
            {{ $t('ui.entities.agreement.status.uncontactable') }}
          </DropdownItem>
          <DropdownItem @item-clicked="$emit('status-changed', 'Contacted')">
            {{ $t('ui.entities.agreement.status.contacted') }}
          </DropdownItem>
          <DropdownItem @item-clicked="$emit('status-changed', 'Appointed')">
            {{ $t('ui.entities.agreement.status.appointed') }}
          </DropdownItem>
          <DropdownItem has-sub-menu>
            {{ $t('ui.entities.agreement.status.complete') }}
            <template #submenu>
              <DropdownItem @item-clicked="$emit('status-changed', 'Dealt')">
                {{ $t('ui.entities.agreement.status.dealt') }}
              </DropdownItem>
              <DropdownItem @item-clicked="$emit('status-changed', 'Refinanced')">
                {{ $t('ui.entities.agreement.status.refinanced') }}
              </DropdownItem>
              <DropdownItem @item-clicked="$emit('status-changed', 'Settled')">
                {{ $t('ui.entities.agreement.status.settled') }}
              </DropdownItem>
              <DropdownItem @item-clicked="$emit('status-changed', 'Handed Back')">
                {{ $t('ui.entities.agreement.status.handed back') }}
              </DropdownItem>
            </template>
          </DropdownItem>
        </Dropdown>
        <div class="w-full mr-4 flex flex-row items-center">
          <Chip
            v-for="tag in tagsToDisplay"
            :key="tag.id"
            :can-remove="!agreementData.markers.archived"
            :severity="tag.displayStyle"
            class="mr-2"
            @remove-chip="$emit('remove-tag', tag.id)"
          >
            {{ tag.title }}
          </Chip>
          <Chip
            v-if="!agreementData.markers.archived"
            class="!text-secondary mr-1 cursor-pointer"
            outlined
            icon="plus"
            :label="$t('ui.task.addTag')"
            @click="$emit('open-tags-modal')"
          />
        </div>
      </div>
      <div class="flex">
        <Badge
          v-if="$can('view_tasks')"
          :content="remindersCounter.total"
          :variant="remindersCounter.overdue > 0 ? 'danger' : 'primary'"
          size="md"
          rounded
        >
          <Button
            v-tippy="{ content: $t('ui.task.reminders') }"
            severity="secondary"
            plain
            icon="check-double"
            @click="$emit('open-reminders-modal')"
          />
        </Badge>
        <Button
          v-if="$can('edit_agreement_details')"
          v-tippy="{ content: $t('ui.actions.edit') }"
          severity="secondary"
          plain
          icon="pencil"
          :router-link="'/agreement-edit/' + agreementData.id"
          :disabled="agreementData.markers.archived"
        />
        <Dropdown>
          <template #triggerContent>
            <Button
              v-tippy="{ content: 'More Actions' }"
              icon="ellipsis-h"
              severity="secondary"
              plain
            />
          </template>
          <DropdownItem @click="$emit('toggle-archive')">
            <Icon type="archive" />
            <span class="w-full">
              {{ agreementData.markers.archived ? $t('ui.actions.unarchive') : $t('ui.actions.archive') }}
            </span>
          </DropdownItem>
          <DropdownItem
            v-if="$can('merge_records')"
            @click="$emit('open-merge-modal')"
          >
            <Icon type="merge" />
            <span class="w-full">{{ $t('ui.actions.merge') }}</span>
          </DropdownItem>
          <DropdownItem
            v-if="$can('transfer_agreements')"
            @click="$emit('open-transfer-modal')"
          >
            <Icon type="arrow-right-from-arc" />
            <span class="w-full">Transfer</span>
          </DropdownItem>
          <DropdownItem @click="$emit('open-decode-vehicle-modal')">
            <Icon type="barcode" />
            <span class="w-full">Decode Vehicle</span>
          </DropdownItem>
          <DropdownItem>
            <Icon type="info-circle" />
            <span class="w-full">Details</span>
          </DropdownItem>
          <DropdownItem
            v-if="$can('delete_records')"
            class="text-error"
            @click="$emit('delete-agreement')"
          >
            <Icon type="trash" />
            <span class="w-full">{{ $t('ui.actions.delete') }}</span>
          </DropdownItem>
        </Dropdown>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// components
import Icon from '@/components/icon/Icon.vue'
import Badge from '@/components/badge/Badge.vue'
import Chip from '@/components/chip/Chip.vue'
import Button from '@/components/button/Button.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
// utilities
import { getDictionaryItem } from '@/dictionary'
// types
import type { Agreement } from '@/types/domain/agreement'

interface Props {
  agreementData: Agreement
  tagsToDisplay: Array<{
    id: number
    title: string
    displayStyle: string
  }>
  remindersCounter: {
    total: number
    overdue: number
  }
}

defineProps<Props>()

defineEmits<{
  'status-changed': [status: string]
  'remove-tag': [tagID: number]
  'open-tags-modal': []
  'open-reminders-modal': []
  'toggle-archive': []
  'open-merge-modal': []
  'open-transfer-modal': []
  'open-decode-vehicle-modal': []
  'delete-agreement': []
}>()
</script>
